import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { track, identify, resetAnalytics, captureUtmsFromUrl, consumeUtms } from "@/lib/analytics";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // The profile row the API returns for the signed-in user. Held here because
  // the terms gate has to be decided before any page paints, and because this
  // fetch already existed for its side effects and was throwing the body away.
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    captureUtmsFromUrl();
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "SIGNED_IN" && s?.user) {
        identify(s.user.id, s.user.email);
        track("sign_in");
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  /* One GET /me per signed-in user, on the sign-in event and on a reload alike.
     It used to run only on SIGNED_IN, so a refreshed tab held no profile at all
     and anything needing one had to fetch it again. It stays a request rather
     than something read off the session: this call also creates the profile row
     on first sign-in and is where the server captures the signup, which is why
     the UTM parameters ride along with it. */
  const userId = session?.user?.id || null;
  useEffect(() => {
    if (!userId) { setProfile(null); return; }
    let alive = true;
    api.get("/me", { params: consumeUtms() })
      .then((r) => { if (alive) setProfile(r.data); })
      .catch(() => { /* the terms gate fails open, see termsRequired below */ });
    return () => { alive = false; };
  }, [userId]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    resetAnalytics();
  };

  // Records acceptance of whichever version the API serves. The response is a
  // full profile, so the gate closes on the same state every other consumer of
  // /me reads, with no refetch.
  const acceptTerms = async () => {
    const { data } = await api.post("/me/terms");
    setProfile(data);
  };

  /* Fails open, deliberately. While the profile is loading, or when the API
     could not be reached at all, the answer is "not required": the alternative
     is standing every user behind a consent screen whose Accept button cannot
     reach the server either, which locks the product for an outage that has
     nothing to do with consent. The record already in Postgres is what makes
     acceptance binding, not this flag. */
  const termsRequired = !!profile?.terms_acceptance_required;

  return (
    <AuthContext.Provider value={{
      session, user: session?.user || null, loading, signOut,
      profile, termsRequired, acceptTerms,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
